import { fbt } from 'fbtee'
import type { UserProfile } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useHistoryState } from '../hooks/useHistoryState'
import { MemberInterestDialog } from './MemberInterestDialog'
import { UserAvatar } from './UserAvatar'

/**
 * Three overlapping member faces (the `first` ones lead) and a +N chip for the
 * rest. Tapping it lists every member — so it has to sit outside any row
 * button. Only an event's context badges `first` as going (`showGoing`).
 */
export function GroupFaceStack({
  memberIds,
  first,
  profiles,
  dialogTitle,
  historyKey,
  showGoing = false,
}: {
  memberIds: string[]
  first?: ReadonlySet<string>
  profiles: Record<string, UserProfile>
  dialogTitle: string
  /** Unique on the page — keeps the dialog open across a profile visit + swipe back. */
  historyKey: string
  showGoing?: boolean
}) {
  const { user } = useAuth()
  const [open, setOpen] = useHistoryState(`faces:${historyKey}`, false)
  const ordered = [...memberIds].sort((a, b) => Number(first?.has(b)) - Number(first?.has(a)))
  const goingIds = showGoing ? ordered.filter(uid => first?.has(uid)) : []
  const otherIds = showGoing ? ordered.filter(uid => !first?.has(uid)) : ordered

  return (
    <>
      <button
        type="button"
        className="flex shrink-0 cursor-pointer -space-x-2"
        data-testid="group-face-stack"
        aria-label={fbt('See members', 'Accessible label for the stacked member faces on a group row')}
        onClick={e => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        {ordered.slice(0, 3).map(uid => (
          <UserAvatar key={uid} profile={profiles[uid]} size="xs" className="ring-2 ring-background" />
        ))}
        {memberIds.length > 3 && (
          <span className="relative flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold text-muted-foreground ring-2 ring-background">
            +{memberIds.length - 3}
          </span>
        )}
      </button>
      <MemberInterestDialog
        open={open}
        onOpenChange={setOpen}
        goingMemberIds={goingIds}
        interestedMemberIds={[]}
        tentativeIds={otherIds}
        profiles={profiles}
        currentUserId={user?.uid}
        name={dialogTitle}
      />
    </>
  )
}
